import { useState, useEffect } from 'react';
import { useCalendarStore } from '@/store/calendarStore';
import { useAuth } from '../contexts/AuthContext';
import storageService from '../services/storageService';
import EventCategoryModal from './EventCategoryModal';
import { FiX, FiSave, FiPlus } from 'react-icons/fi';

/**
 * Formulario para crear/editar Citas, Lecturas y Eventos
 * Guarda en BD local (IndexedDB) para usuarios registrados sin permisos
 * Guarda en BD principal (backend) para calendarizadores/admin
 */
export default function EventForm({ type = 'evento', item = null, onClose }) {
  const { addCita, addLectura, addEvento, updateCita, updateLectura, updateEvento, loadEventos } = useCalendarStore();
  const { user } = useAuth();
  
  const isEditing = item !== null;
  
  // Verificar permisos del usuario
  const canSaveToMain = user?.rol === 'administrador' || user?.modulePermissions?.some(
    p => p.moduleName === 'calendario' && p.roleName === 'calendarizador'
  );
  
  // Inicializar servicio de almacenamiento local
  useEffect(() => {
    if (!canSaveToMain) {
      storageService.init().catch(err => {
        console.error('Error al inicializar storage:', err);
      });
    }
  }, [canSaveToMain]);
  
  const getInitialFormData = () => {
    if (item) return item;
    
    const baseData = {
      id: Date.now().toString(),
      color: '#4A90E2',
      icono: 'calendar',
    };
    
    if (type === 'cita') {
      return {
        ...baseData,
        nombre: '',
        tipo: 'personal',
        descripcion: '',
        cita: '',
        duracion: 60,
        fechaInicio: new Date().toISOString().slice(0, 16),
        fechaFin: new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16),
      };
    } else if (type === 'lectura') {
      return {
        ...baseData,
        tipo: 'Torah',
        subtipo: 'Parasha',
        descripcion: '',
        referencia: '',
        fecha: new Date().toISOString().slice(0, 10),
      };
    } else {
      return {
        ...baseData,
        nombre: '',
        descripcion: '',
        tipo: 'festividad',
        subtipo: '',
        palabrasClave: [],
        categoria: 'religiosa',
        fechaInicio: new Date().toISOString().slice(0, 16),
        fechaFin: new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16),
        duracion: 60,
      };
    }
  };
  
  const [formData, setFormData] = useState(getInitialFormData());
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryField, setCategoryField] = useState(null); // 'tipo', 'subtipo', o 'categoria'

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleOpenCategoryModal = (field) => {
    setCategoryField(field);
    setShowCategoryModal(true);
  };

  const handleSelectCategory = (category) => {
    // Actualizar el campo correspondiente
    setFormData(prev => ({
      ...prev,
      [categoryField]: category.nombre,
      color: category.color // Opcional: usar el color de la categoría
    }));
    setShowCategoryModal(false);
    setCategoryField(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (type === 'evento' && !canSaveToMain) {
        // ✅ USUARIO REGISTRADO SIN PERMISOS: Guardar en IndexedDB local
        console.log('💾 Guardando evento en almacenamiento local (IndexedDB)');
        
        const eventoData = {
          ...formData,
          estado: 'borrador_local',
          userId: user?.id
        };
        
        if (isEditing && formData.localId) {
          // Actualizar evento local
          await storageService.eventos.actualizar(formData.localId, eventoData);
          console.log('✅ Evento local actualizado:', formData.localId);
        } else {
          // Crear nuevo evento local
          const localId = await storageService.eventos.agregar(eventoData);
          console.log('✅ Evento local creado con ID:', localId);
        }
        
        // Recargar el store para mostrar los eventos locales
        await loadEventos();
        alert('✅ Evento guardado localmente en tu navegador');
      } else {
        // ✅ ADMIN/CALENDARIZADOR: Guardar en BD principal (backend)
        console.log('🌐 Guardando en BD principal (backend)');
        
        if (isEditing) {
          if (type === 'cita') updateCita(formData.id, formData);
          else if (type === 'lectura') updateLectura(formData.id, formData);
          else updateEvento(formData.id, formData);
        } else {
          if (type === 'cita') addCita(formData);
          else if (type === 'lectura') addLectura(formData);
          else addEvento(formData);
        }
      }
      
      onClose();
    } catch (error) {
      console.error('❌ Error al guardar:', error);
      alert('Error al guardar. Por favor intenta nuevamente.');
    }
  };

  const getTitle = () => {
    const action = isEditing ? 'Editar' : 'Nueva';
    if (type === 'cita') return `${action} Cita Ordenada`;
    if (type === 'lectura') return `${action} Lectura`;
    return `${action} Evento`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold">{getTitle()}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Campos comunes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Color
            </label>
            <input
              type="color"
              name="color"
              value={formData.color}
              onChange={handleChange}
              className="w-full h-10 rounded border border-gray-300"
            />
          </div>

          {/* Campos específicos para Citas */}
          {type === 'cita' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo
                </label>
                <input
                  type="text"
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción *
                </label>
                <textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  required
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cita Bíblica
                </label>
                <input
                  type="text"
                  name="cita"
                  value={formData.cita}
                  onChange={handleChange}
                  placeholder="Ej: Éxodo 20:8"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha y Hora Inicio *
                  </label>
                  <input
                    type="datetime-local"
                    name="fechaInicio"
                    value={formData.fechaInicio}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha y Hora Fin *
                  </label>
                  <input
                    type="datetime-local"
                    name="fechaFin"
                    value={formData.fechaFin}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
            </>
          )}

          {/* Campos específicos para Lecturas */}
          {type === 'lectura' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo *
                  </label>
                  <input
                    type="text"
                    name="tipo"
                    value={formData.tipo}
                    onChange={handleChange}
                    required
                    placeholder="Ej: Torah"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subtipo *
                  </label>
                  <input
                    type="text"
                    name="subtipo"
                    value={formData.subtipo}
                    onChange={handleChange}
                    required
                    placeholder="Ej: Parasha"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción *
                </label>
                <textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  required
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Referencia *
                </label>
                <input
                  type="text"
                  name="referencia"
                  value={formData.referencia}
                  onChange={handleChange}
                  required
                  placeholder="Ej: Génesis 1:1-6:8"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha *
                </label>
                <input
                  type="date"
                  name="fecha"
                  value={formData.fecha}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </>
          )}

          {/* Campos específicos para Eventos */}
          {type === 'evento' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción *
                </label>
                <textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  required
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      name="tipo"
                      value={formData.tipo}
                      onChange={handleChange}
                      required
                      placeholder="Selecciona o escribe..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => handleOpenCategoryModal('tipo')}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      title="Gestionar tipos"
                    >
                      <FiPlus className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subtipo
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      name="subtipo"
                      value={formData.subtipo}
                      onChange={handleChange}
                      placeholder="Selecciona o escribe..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => handleOpenCategoryModal('subtipo')}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      title="Gestionar subtipos"
                    >
                      <FiPlus className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoría *
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="categoria"
                    value={formData.categoria}
                    onChange={handleChange}
                    required
                    placeholder="Selecciona o escribe..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => handleOpenCategoryModal('categoria')}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    title="Gestionar categorías"
                  >
                    <FiPlus className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha y Hora Inicio *
                  </label>
                  <input
                    type="datetime-local"
                    name="fechaInicio"
                    value={formData.fechaInicio}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha y Hora Fin *
                  </label>
                  <input
                    type="datetime-local"
                    name="fechaFin"
                    value={formData.fechaFin}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
            </>
          )}

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
            >
              <FiSave className="w-4 h-4" />
              {isEditing ? 'Actualizar' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>

      {/* Modal de gestión de categorías */}
      {showCategoryModal && (
        <EventCategoryModal
          onClose={() => {
            setShowCategoryModal(false);
            setCategoryField(null);
          }}
          onSelect={handleSelectCategory}
        />
      )}
    </div>
  );
}













